import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Loader2, Sparkles, ArrowLeftRight, Layers, SplitSquareHorizontal, ScanSearch, AlertTriangle } from 'lucide-react';
import { CATEGORIAS, dec, fechaCorta } from '../../lib/oftalmo';
import { CapaMedicion, urlImagen } from './VisorImagen';

const MODOS = [
  ['lado', 'Lado a lado', ArrowLeftRight],
  ['superponer', 'Superpuestas', Layers],
  ['cortinilla', 'Cortinilla', SplitSquareHorizontal],
  ['cambios', 'Detección de cambios', ScanSearch],
];
const NIVEL = {
  sin_cambios: ['Sin cambios relevantes', 'bg-emerald-100 text-emerald-900'],
  leve: ['Cambios leves', 'bg-amber-100 text-amber-900'],
  moderado: ['Cambios moderados', 'bg-orange-100 text-orange-900'],
  marcado: ['Cambios marcados', 'bg-red-100 text-red-900'],
};

/**
 * Dos imágenes del mismo ojo y la misma prueba, de dos fechas. El servidor
 * las alinea (giro, escala y desplazamiento de la cámara) y marca lo que ha
 * cambiado; aquí se ven lado a lado, una encima de otra con transparencia, con
 * una cortinilla o con el mapa de cambios. Si las dos tienen la lesión
 * medida, se pintan los dos contornos juntos y cuánto ha cambiado el área.
 */
export default function Comparador({ pid, imagenes, inicial, open, onOpenChange, iaActiva, puedeEditar }) {
  const [aId, setAId] = useState(inicial?.[0]);
  const [bId, setBId] = useState(inicial?.[1]);
  const [modo, setModo] = useState('superponer');
  const [res, setRes] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [opacidad, setOpacidad] = useState(50);
  const [corte, setCorte] = useState(50);
  const [verCapa, setVerCapa] = useState(true);
  const [ia, setIa] = useState(null);
  const [iaCargando, setIaCargando] = useState(false);

  useEffect(() => { if (open) { setAId(inicial?.[0]); setBId(inicial?.[1]); setIa(null); } }, [open, inicial]);
  const a = imagenes.find((i) => i.id === aId);
  const b = imagenes.find((i) => i.id === bId);
  // Para elegir: las de la misma prueba y el mismo ojo que la de referencia.
  const hermanas = useMemo(() => imagenes.filter((i) => a && i.categoria === a.categoria && (!a.ojo || i.ojo === a.ojo))
    .sort((x, y) => (x.fecha || '').localeCompare(y.fecha || '')), [imagenes, a]);

  useEffect(() => {
    if (!open || !aId || !bId) return undefined;
    let vivo = true;
    setCargando(true);
    setRes(null);
    api.post(`/api/patients/${pid}/oftalmologia/comparar`, { a: aId, b: bId })
      .then((r) => { if (vivo) setRes(r.data); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'No se pudieron comparar'))
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [open, pid, aId, bId]);

  const pedirIa = async () => {
    setIaCargando(true);
    try {
      const r = await api.post(`/api/patients/${pid}/oftalmologia/comparar/ia`, { a: aId, b: bId }, { timeout: 180000 });
      setIa(r.data.texto);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'La IA no ha podido leer las imágenes');
    } finally {
      setIaCargando(false);
    }
  };

  if (!a || !b) return null;
  const medidas = res?.medidas || {};
  const refUrl = res?.referencia || urlImagen(pid, a.id);
  const aliUrl = res?.alineada || urlImagen(pid, b.id);
  const Contornos = () => (
    <>
      {medidas.a && <CapaMedicion medicion={{ puntos: medidas.a.puntos }} color="#FACC15" mostrarRef={false} />}
      {medidas.b && <CapaMedicion medicion={{ puntos: medidas.b.puntos }} color="#22C55E" mostrarRef={false} />}
    </>
  );
  const selector = (valor, set, etiqueta) => (
    <label className="text-xs text-muted-foreground flex flex-col gap-1">{etiqueta}
      <select value={valor} onChange={(e) => set(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground">
        {hermanas.map((i) => <option key={i.id} value={i.id}>{fechaCorta(i.fecha)} {i.ojo} {i.valores?.cst ? `· ${i.valores.cst} µm` : ''}{i.valores?.kmax ? `· Kmax ${i.valores.kmax}` : ''}</option>)}
      </select>
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(1320px,97vw)] max-h-[95vh] overflow-y-auto" data-testid="oft-comparador">
        <DialogTitle>Comparar · {CATEGORIAS[a.categoria]} {a.ojo}</DialogTitle>
        <div className="flex flex-wrap items-end gap-3">
          {selector(aId, setAId, 'Referencia (antes)')}
          {selector(bId, setBId, 'Comparar con (después)')}
          <div className="flex flex-wrap gap-1 ml-auto">
            {MODOS.map(([k, t, Icono]) => (
              <Button key={k} size="sm" variant={modo === k ? 'default' : 'outline'} onClick={() => setModo(k)} data-testid={`oft-modo-${k}`}>
                <Icono className="w-4 h-4 mr-1" />{t}
              </Button>
            ))}
          </div>
        </div>
        {res?.aviso && <div className="text-sm text-amber-700 flex items-center gap-1"><AlertTriangle className="w-4 h-4" />{res.aviso}</div>}

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-4">
          <div className="bg-black rounded-lg p-2 flex items-center justify-center min-h-[320px]">
            {modo === 'lado' && (
              <div className="grid grid-cols-2 gap-2 w-full">
                {[a, b].map((x, i) => (
                  <div key={x.id} className="relative">
                    <img src={i === 0 ? refUrl : aliUrl} alt="" className="w-full block" />
                    {i === 0 && medidas.a && <CapaMedicion medicion={{ puntos: medidas.a.puntos }} color="#FACC15" mostrarRef={false} />}
                    {i === 1 && medidas.b && <CapaMedicion medicion={{ puntos: medidas.b.puntos }} color="#22C55E" mostrarRef={false} />}
                    <div className="absolute top-1 left-1 bg-black/70 text-white text-xs px-2 py-0.5 rounded">{fechaCorta(x.fecha)}</div>
                  </div>
                ))}
              </div>
            )}
            {modo === 'superponer' && (
              <div className="relative inline-block max-w-full" data-testid="oft-superpuestas">
                <img src={refUrl} alt="" className="block max-h-[70vh] max-w-full" />
                <img src={aliUrl} alt="" className="absolute inset-0 w-full h-full" style={{ opacity: opacidad / 100 }} />
                <Contornos />
              </div>
            )}
            {modo === 'cortinilla' && (
              <div className="relative inline-block max-w-full">
                <img src={refUrl} alt="" className="block max-h-[70vh] max-w-full" />
                <img src={aliUrl} alt="" className="absolute inset-0 w-full h-full" style={{ clipPath: `inset(0 0 0 ${corte}%)` }} />
                <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow" style={{ left: `${corte}%` }} />
                <Contornos />
              </div>
            )}
            {modo === 'cambios' && (
              <div className="relative inline-block max-w-full" data-testid="oft-mapa-cambios">
                <img src={refUrl} alt="" className="block max-h-[70vh] max-w-full" />
                {res?.capa_cambios && verCapa && <img src={res.capa_cambios} alt="" className="absolute inset-0 w-full h-full" />}
                {res && verCapa && (res.zonas || []).slice(0, 12).map((z, i) => (
                  <div key={i} className="absolute text-[10px] font-bold text-white bg-black/70 rounded px-1"
                    style={{ left: `${z.x + z.ancho}%`, top: `${z.y}%` }}>{i + 1}</div>
                ))}
              </div>
            )}
            {cargando && modo !== 'lado' && (
              <div className="absolute"><Loader2 className="w-8 h-8 text-white animate-spin" /></div>
            )}
          </div>

          <div className="space-y-3 text-sm">
            {(modo === 'superponer' || modo === 'cortinilla') && (
              <label className="block text-xs text-muted-foreground">
                {modo === 'superponer' ? `Transparencia de la posterior: ${opacidad} %` : 'Desliza la cortinilla'}
                <input type="range" min={0} max={100} value={modo === 'superponer' ? opacidad : corte}
                  onChange={(e) => (modo === 'superponer' ? setOpacidad(Number(e.target.value)) : setCorte(Number(e.target.value)))}
                  className="w-full" data-testid="oft-deslizador" />
              </label>
            )}
            {res && (
              <div className="rounded-lg border p-3 space-y-1">
                <div className="font-semibold">Alineación</div>
                <div className="text-xs text-muted-foreground">
                  {res.alineacion.calidad === 'buena' ? `Automática, buena (${res.alineacion.puntos} puntos en común)`
                    : res.alineacion.calidad === 'regular' ? 'Automática, aproximada' : 'No se pudo alinear: se comparan tal cual'}
                </div>
              </div>
            )}
            {(medidas.a || medidas.b) && (
              <div className="rounded-lg border p-3 space-y-1" data-testid="oft-medidas-comparadas">
                <div className="font-semibold">Lesión medida</div>
                {medidas.a && <div><span className="inline-block w-3 h-3 rounded-sm bg-yellow-400 mr-1 align-middle" />{fechaCorta(a.fecha)}: <b>{medidas.a.area_mm2 !== null ? `${dec(medidas.a.area_mm2, 2)} mm²` : `${dec(medidas.a.area_pct, 2)} %`}</b></div>}
                {medidas.b && <div><span className="inline-block w-3 h-3 rounded-sm bg-green-500 mr-1 align-middle" />{fechaCorta(b.fecha)}: <b>{medidas.b.area_mm2 !== null ? `${dec(medidas.b.area_mm2, 2)} mm²` : `${dec(medidas.b.area_pct, 2)} %`}</b></div>}
                {medidas.cambio_pct !== undefined && (
                  <div className={`font-semibold ${medidas.cambio_pct < 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {medidas.cambio_pct < 0 ? `Se ha reducido un ${dec(-medidas.cambio_pct, 1)} %` : `Ha crecido un ${dec(medidas.cambio_pct, 1)} %`}
                  </div>
                )}
              </div>
            )}
            {modo === 'cambios' && res && (
              <div className="rounded-lg border p-3 space-y-2" data-testid="oft-resultado-cambios">
                <div className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${NIVEL[res.nivel]?.[1]}`}>{NIVEL[res.nivel]?.[0]}</div>
                <div className="text-xs text-muted-foreground">
                  {res.n_zonas} zona{res.n_zonas === 1 ? '' : 's'} · {dec(res.cambio_pct, 2)} % de la imagen
                </div>
                <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={verCapa} onChange={(e) => setVerCapa(e.target.checked)} />Ver el mapa</label>
                <div className="text-xs space-y-0.5">
                  <div><span className="inline-block w-3 h-3 rounded-sm mr-1 align-middle" style={{ background: '#EB2828' }} />Más oscura en la posterior (sangre, pigmento, líquido…)</div>
                  <div><span className="inline-block w-3 h-3 rounded-sm mr-1 align-middle" style={{ background: '#FFC81E' }} />Más clara en la posterior (exudado, drusa, atrofia, reabsorción…)</div>
                </div>
                <ol className="text-xs list-decimal pl-4 max-h-40 overflow-y-auto">
                  {(res.zonas || []).slice(0, 12).map((z, i) => (
                    <li key={i}>{z.cambio === 'mas_oscura' ? 'Más oscura' : 'Más clara'} · {dec(z.area_pct, 2)} %</li>
                  ))}
                </ol>
                <div className="text-[11px] text-muted-foreground">
                  Ayuda para mirar: señala dónde ha cambiado la imagen, no qué es. Se calcula en el servidor de la clínica, sin enviar la imagen fuera.
                </div>
              </div>
            )}
            {puedeEditar && (
              <div className="rounded-lg border p-3 space-y-2">
                <Button size="sm" variant="outline" onClick={pedirIa} disabled={!iaActiva || iaCargando} data-testid="oft-ia">
                  {iaCargando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                  Lectura con IA
                </Button>
                {!iaActiva && <div className="text-[11px] text-muted-foreground">La IA está apagada en esta instalación. La detección de cambios funciona igual.</div>}
                {ia && <div className="text-xs whitespace-pre-wrap" data-testid="oft-ia-texto">{ia}</div>}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
